import * as React from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";

import { cn } from "@/lib/utils";
import { ChevronDownIcon, XIcon, CheckIcon, Loader2Icon } from "lucide-react";
import { Badge, BadgeProps, Button, Input, InputGroup } from ".";
import { isEqual, get } from "lodash";
import { Tooltip } from "./tooltip";

const ComboboxOpenOnFocusContext = React.createContext<(() => void) | null>(null);
const ComboboxSearchContext = React.createContext<((query: string) => void) | null>(null);
const ComboboxLoadingContext = React.createContext<boolean>(false);

interface AddItemOption {
  __addItem: true;
  query: string;
}

function isAddItemOption<Value>(value: Value | AddItemOption): value is AddItemOption {
  return (
    typeof value === "object" && value !== null && "__addItem" in value && (value as AddItemOption).__addItem === true
  );
}

function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  return instance => {
    for (const ref of refs) {
      if (ref == null) continue;
      if (typeof ref === "function") {
        ref(instance);
      } else {
        (ref as React.MutableRefObject<T | null>).current = instance;
      }
    }
  };
}

type RenderMenuItemProps<Value> = {
  item: Value;
  index: number;
  label: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
};

function Combobox<Value, Multiple extends boolean | undefined = false>({
  children,
  placeholder,
  showClear = false,
  searchable = false,
  hideTrigger = false,
  emptyText = "No items found.",
  onOpenChange,
  itemToStringLabel,
  itemToLabel,
  isItemEqualToValue = (current: Value, selected: Value) => isEqual(current, selected),
  itemToIcon = (item: Value) => get(item, "icon") as React.ReactNode,
  itemToBadge = (item: Value) => get(item, "badge") as BadgeProps | React.ReactNode | undefined,
  renderMenuItem,
  multiple,
  value,
  className,
  maxChips,
  onLoadMore,
  hasMore,
  isLoadingMore,
  onSearch,
  searchDebounce = 300,
  allowAdditions = false,
  addItemLabel = (query: string) => `Add "${query}"`,
  onAddItem,
  ...props
}: Omit<ComboboxPrimitive.Root.Props<Value, Multiple>, "className"> & {
  placeholder?: string;
  emptyText?: string;
  showClear?: boolean;
  searchable?: boolean;
  hideTrigger?: boolean;
  itemToIcon?: (item: Value) => React.ReactNode;
  itemToLabel?: (item: Value) => React.ReactNode;
  itemToBadge?: (item: Value) => BadgeProps | React.ReactNode | undefined;
  renderMenuItem?: (props: RenderMenuItemProps<Value>) => React.ReactNode;
  className?: string;
  maxChips?: number | "auto";
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onSearch?: (query: string) => void;
  searchDebounce?: number;
  allowAdditions?: boolean;
  addItemLabel?: (query: string) => string;
  onAddItem?: (query: string) => Value | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  // Async/server-side search: control inputValue so Base UI doesn't reset it when `items` change.
  // Require searchable — callers often spread paginated combobox props (including onSearch)
  // onto a non-searchable select; that must not trap the display label in searchQuery.
  const isAsyncSearch = Boolean(onSearch) && searchable;

  const onSearchRef = React.useRef(onSearch);
  onSearchRef.current = onSearch;
  const searchDebounceRef = React.useRef(searchDebounce);
  searchDebounceRef.current = searchDebounce;
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isSearchPending, setIsSearchPending] = React.useState(false);
  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setIsSearchPending(true);
    searchTimerRef.current = setTimeout(() => {
      onSearchRef.current?.(query);
      setIsSearchPending(false);
    }, searchDebounceRef.current);
  }, []);

  React.useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    },
    []
  );
  React.useEffect(() => {
    if (!isLoadingMore) setIsSearchPending(false);
  }, [isLoadingMore]);

  const anchorRef = useComboboxAnchor<HTMLDivElement>();
  const [computedMaxChips, setComputedMaxChips] = React.useState<number>();

  const effectiveMaxChips = maxChips === "auto" ? computedMaxChips : maxChips;
  const [chipFocused, setChipFocused] = React.useState(false);
  const interacting = open || chipFocused;

  const getItemLabel = (item: Value | AddItemOption) => {
    if (isAddItemOption(item)) return item.query;
    if (itemToStringLabel) return itemToStringLabel(item);
    return get(item, "label", String(item)) as unknown as string;
  };

  const hasChips = Array.isArray(value) && value.length > 0;
  const prevChipCount = React.useRef(Array.isArray(value) ? value.length : 0);

  const {
    items: propsItems,
    onValueChange: propsOnValueChange,
    disabled,
    inputValue: inputValueProp,
    onInputValueChange: onInputValueChangeProp,
    filter: filterProp,
    ...rootProps
  } = props;
  const disabledStyles =
    "data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-transparent dark:data-[disabled=true]:bg-input/80";
  const disabledChildOpacity =
    "data-[disabled=true]:[&_[data-slot=combobox-trigger]]:opacity-50 data-[disabled=true]:[&_[data-slot=combobox-chip]]:opacity-50";
  const rootList: (Value | AddItemOption)[] = [...(propsItems || [])];
  var showAddOption = false;
  if (allowAdditions && searchable && searchQuery.length > 0 && !isSearchPending && !isLoadingMore) {
    const hasExactMatch = searchQuery.length > 0 && propsItems?.some(item => getItemLabel(item) === searchQuery);
    if (!hasExactMatch) {
      rootList.push({ __addItem: true, query: searchQuery });
      showAddOption = true;
    }
  }

  type OnValueChangeArgTypes = Parameters<NonNullable<typeof propsOnValueChange>>;
  const handleValueChange = React.useCallback(
    (newValue: OnValueChangeArgTypes[0], eventDetails: OnValueChangeArgTypes[1]) => {
      var effectiveNewValue: OnValueChangeArgTypes[0] | undefined = newValue;
      if (showAddOption) {
        if (multiple && Array.isArray(newValue)) {
          const addItemOption = newValue.find(v => isAddItemOption(v));
          if (addItemOption) {
            const newItem = onAddItem?.(addItemOption.query);
            if (newItem) {
              effectiveNewValue = [...newValue.filter(v => !isAddItemOption(v)), newItem] as OnValueChangeArgTypes[0];
            } else {
              effectiveNewValue = undefined;
            }
          }
        } else if (!multiple && isAddItemOption(newValue)) {
          effectiveNewValue = onAddItem?.(newValue.query) as OnValueChangeArgTypes[0] | undefined;
        }
      }
      if (effectiveNewValue !== undefined) {
        propsOnValueChange?.(effectiveNewValue, eventDetails);
      }
    },
    [onAddItem, propsOnValueChange, multiple, showAddOption]
  );

  const handleInputValueChange = React.useCallback(
    (next: string, details: Parameters<NonNullable<typeof onInputValueChangeProp>>[1]) => {
      if (isAsyncSearch) {
        const reason = details?.reason;
        if (reason === "input-change" || reason === "input-clear") {
          handleSearch(next);
        } else {
          setSearchQuery(next);
          if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
          setIsSearchPending(false);
        }
      }
      onInputValueChangeProp?.(next, details);
    },
    [isAsyncSearch, handleSearch, onInputValueChangeProp]
  );

  const selectedId =
    value == null
      ? null
      : typeof value === "object" && value !== null && "id" in value
      ? (value as { id: unknown }).id
      : value;
  React.useEffect(() => {
    if (!isAsyncSearch || multiple) return;
    const next =
      value == null
        ? ""
        : itemToStringLabel
        ? itemToStringLabel(value as Value)
        : (get(value, "label", String(value)) as string);
    setSearchQuery(next);
  }, [isAsyncSearch, multiple, selectedId]);

  // Reset computed max when value/interaction changes so all chips render for measurement
  React.useLayoutEffect(() => {
    if (maxChips === "auto" && !interacting) {
      setComputedMaxChips(undefined);
    }
  }, [value, interacting, maxChips]);

  // Measure how many chips fit once they're all rendered (computedMaxChips === undefined)
  React.useLayoutEffect(() => {
    if (maxChips !== "auto" || interacting || computedMaxChips !== undefined) return;
    if (!anchorRef.current || !Array.isArray(value) || value.length === 0) return;

    const container = anchorRef.current.querySelector<HTMLElement>('[data-slot="combobox-chips"]');
    if (!container) return;

    const chips = Array.from(container.querySelectorAll<HTMLElement>('[data-slot="combobox-chip"]'));
    if (chips.length === 0) return;

    // Use the constrained anchor width — chips container grows with content during measurement.
    const trailingControls = Array.from(anchorRef.current.children).slice(1) as HTMLElement[];
    const trailingWidth = trailingControls.reduce((sum, el) => sum + el.offsetWidth, 0);
    const gap = parseFloat(getComputedStyle(container).gap) || 4;
    const overflowReserve = 56;
    const availableWidth = Math.max(0, anchorRef.current.clientWidth - trailingWidth - overflowReserve);

    let usedWidth = 0;
    let fitCount = 0;

    for (const chip of chips) {
      const width = chip.offsetWidth + (fitCount > 0 ? gap : 0);
      if (fitCount > 0 && usedWidth + width > availableWidth) break;
      usedWidth += width;
      fitCount++;
    }

    const newMax = fitCount < value.length ? Math.max(1, fitCount) : undefined;
    setComputedMaxChips(newMax);
  }, [maxChips, interacting, value, computedMaxChips]);

  // Recalculate on container resize
  React.useEffect(() => {
    if (maxChips !== "auto" || interacting) return;
    if (!anchorRef.current) return;

    const el = anchorRef.current;
    let prevWidth = el.offsetWidth;

    const observer = new ResizeObserver(() => {
      const newWidth = el.offsetWidth;
      if (newWidth !== prevWidth) {
        prevWidth = newWidth;
        setComputedMaxChips(undefined);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [maxChips, interacting]);

  // Scroll to end when chips are added
  React.useEffect(() => {
    const currentCount = Array.isArray(value) ? value.length : 0;
    if (multiple && anchorRef.current && currentCount > prevChipCount.current) {
      const scrollable = anchorRef.current.querySelector<HTMLElement>('[data-slot="combobox-chips"]');
      if (scrollable) {
        scrollable.scrollLeft = scrollable.scrollWidth;
      }
    }
    prevChipCount.current = currentCount;
  }, [value, multiple]);

  // Scroll to start when not interacting
  React.useEffect(() => {
    if (!interacting && multiple && anchorRef.current) {
      const scrollable = anchorRef.current.querySelector<HTMLElement>('[data-slot="combobox-chips"]');
      if (scrollable) {
        scrollable.scrollLeft = 0;
      }
    }
  }, [interacting, multiple]);

  const getDropdownContent = (anchor?: React.MutableRefObject<HTMLElement | null>) => {
    const renderComboboxItem = (item: Value | AddItemOption, index: number) => {
      if (typeof item === "string" && item.startsWith("__group_header__")) {
        const groupLabel = item.replace("__group_header__", "");
        return (
          <div
            key={index}
            className="text-slate-400 dark:text-slate-500 px-2.5 py-1.5 text-[9px] font-extrabold uppercase tracking-widest select-none bg-slate-50/70 dark:bg-slate-900/50 border-y border-slate-100/30 dark:border-slate-800/50 mt-1.5 first:mt-0"
          >
            {groupLabel}
          </div>
        );
      }
      if (isAddItemOption(item)) {
        return (
          <ComboboxItem key={index} value={item}>
            {addItemLabel(item.query)}
          </ComboboxItem>
        );
      }
      const icon = itemToIcon?.(item as Value);
      var badgeObject = itemToBadge?.(item as Value);
      var badge: React.ReactNode = null;
      if (typeof badgeObject === "string") {
        badge = (
          <Badge variant="label" className="text-xs">
            {badgeObject}
          </Badge>
        );
      } else if (typeof badgeObject === "object" && badgeObject !== null) {
        const badgeProps = { ...badgeObject } as BadgeProps;
        badgeProps.variant = badgeProps.variant ?? "label";
        badgeProps.className = cn("text-xs", badgeProps.className);
        badge = <Badge {...badgeProps} />;
      }
      const label = getItemLabel(item);

      var content: React.ReactNode;
      if (renderMenuItem) {
        content = renderMenuItem({ item, index, label, icon, badge });
      }
      if (content === undefined) {
        content = (
          <>
            {icon}
            {badge}
            {itemToLabel?.(item as Value) ?? label}
          </>
        );
      }
      return (
        <ComboboxItem key={index} value={item}>
          {content}
        </ComboboxItem>
      );
    };

    return (
      <ComboboxContent anchor={anchor}>
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          isSearchPending={isSearchPending}
        >
          {renderComboboxItem}
        </ComboboxList>
      </ComboboxContent>
    );
  };

  return (
    <ComboboxLoadingContext.Provider value={isSearchPending || !!isLoadingMore}>
      <ComboboxSearchContext.Provider value={handleSearch}>
        <ComboboxOpenOnFocusContext.Provider value={() => setOpen(true)}>
          <ComboboxPrimitive.Root
            open={open}
            onOpenChange={(nextOpen, details) => {
              setOpen(nextOpen);
              onOpenChange?.(nextOpen, details);
            }}
            itemToStringLabel={getItemLabel}
            isItemEqualToValue={isItemEqualToValue}
            multiple={multiple}
            value={value}
            disabled={disabled}
            {...rootProps}
            inputValue={isAsyncSearch ? searchQuery : inputValueProp}
            onInputValueChange={handleInputValueChange}
            filter={isAsyncSearch ? null : filterProp}
            onValueChange={handleValueChange}
            items={rootList}
          >
            {children ??
              (multiple ? (
                <div
                  ref={anchorRef}
                  data-disabled={disabled ? "true" : undefined}
                  className={cn(
                    "group/input-group dark:bg-input/30 border-input focus-within:border-ring focus-within:ring-ring/50 has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40 has-aria-invalid:border-destructive dark:has-aria-invalid:border-destructive/50 flex min-h-9 min-w-0 max-w-full items-center gap-0.5 overflow-hidden rounded-md border bg-transparent bg-clip-padding text-sm transition-colors focus-within:ring-3 has-aria-invalid:ring-3",
                    disabledStyles,
                    disabledChildOpacity,
                    className
                  )}
                >
                  <ComboboxChips
                    className="flex-1 min-w-0 border-0 rounded-none shadow-none ring-0 focus-within:ring-0 focus-within:border-transparent"
                    onFocus={e => {
                      if ((e.target as HTMLElement).closest('[data-slot="combobox-chip"]')) {
                        setChipFocused(true);
                      }
                    }}
                    onBlur={e => {
                      const related = e.relatedTarget as HTMLElement | null;
                      if (!related?.closest('[data-slot="combobox-chip"]')) {
                        setChipFocused(false);
                      }
                    }}
                  >
                    {Array.isArray(value) &&
                      (() => {
                        const truncate =
                          !interacting && effectiveMaxChips !== undefined && value.length > effectiveMaxChips;
                        const visibleChips = truncate ? value.slice(0, effectiveMaxChips) : value;
                        const overflowCount = truncate ? value.length - effectiveMaxChips : 0;
                        const overflowChips = truncate ? value.slice(effectiveMaxChips).map(getItemLabel) : [];
                        return (
                          <>
                            {visibleChips.map((chip: Value, i: number) => (
                              <ComboboxChip key={i}>
                                {itemToIcon?.(chip)}
                                {itemToLabel?.(chip) ?? getItemLabel(chip)}
                              </ComboboxChip>
                            ))}
                            {overflowCount > 0 && (
                              <Tooltip>
                                <Tooltip.Trigger
                                  onClick={() => {
                                    anchorRef.current?.querySelector<HTMLInputElement>("input")?.focus();
                                  }}
                                  render={
                                    <span
                                      data-slot="combobox-chip-count"
                                      className="text-muted-foreground flex h-[calc(--spacing(5.25))] w-fit items-center justify-center px-1 text-xs font-medium whitespace-nowrap cursor-pointer"
                                    >
                                      +{overflowCount} more
                                    </span>
                                  }
                                />
                                <Tooltip.Content>
                                  <div className="flex max-h-60 w-40 flex-col gap-1 overflow-y-auto">
                                    {overflowChips.map((chip, i) => (
                                      <div key={i} className="truncate text-xs">
                                        {chip}
                                      </div>
                                    ))}
                                  </div>
                                </Tooltip.Content>
                              </Tooltip>
                            )}
                          </>
                        );
                      })()}
                    <ComboboxChipsInput
                      disabled={disabled}
                      hideTrigger
                      placeholder={!hasChips ? placeholder : undefined}
                      searchable={searchable}
                    />
                  </ComboboxChips>
                  <div className="shrink-0 flex items-center pr-1">
                    <ComboboxPrimitive.Trigger
                      data-slot="combobox-trigger"
                      render={
                        <InputGroup.Button
                          size="icon-xs"
                          variant="ghost"
                          data-slot="input-group-button"
                          className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent [&_svg:not([class*='size-'])]:size-4"
                        />
                      }
                    >
                      <ChevronDownIcon className="text-muted-foreground size-4 pointer-events-none" />
                    </ComboboxPrimitive.Trigger>
                    {showClear && <ComboboxClear />}
                  </div>
                </div>
              ) : (
                <ComboboxInput
                  ref={anchorRef}
                  disabled={disabled}
                  className={className}
                  placeholder={placeholder}
                  showClear={showClear}
                  searchable={searchable}
                  hideTrigger={hideTrigger}
                  icon={value != null && itemToIcon ? itemToIcon(value as Value) : undefined}
                />
              ))}
            {getDropdownContent(anchorRef)}
          </ComboboxPrimitive.Root>
        </ComboboxOpenOnFocusContext.Provider>
      </ComboboxSearchContext.Provider>
    </ComboboxLoadingContext.Provider>
  );
}

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      {children}
      <ChevronDownIcon className="text-muted-foreground size-4 pointer-events-none" />
    </ComboboxPrimitive.Trigger>
  );
}

function ComboboxClear({ className, onClick, ...props }: ComboboxPrimitive.Clear.Props) {
  const openOnFocus = React.useContext(ComboboxOpenOnFocusContext);
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      className={cn(className)}
      onClick={e => {
        onClick?.(e);
        openOnFocus?.();
      }}
      {...props}
      render={
        <InputGroup.Button variant="ghost" size="icon-xs">
          <XIcon />
        </InputGroup.Button>
      }
      tabIndex={undefined}
    />
  );
}

const ComboboxInput = React.forwardRef<
  HTMLDivElement,
  ComboboxPrimitive.Input.Props & {
    hideTrigger?: boolean;
    showClear?: boolean;
    searchable?: boolean;
    icon?: React.ReactNode;
  }
>(function ComboboxInput(
  { className, disabled = false, hideTrigger = false, showClear = false, searchable = false, icon, onFocus, ...props },
  forwardedRef
) {
  const openOnFocus = React.useContext(ComboboxOpenOnFocusContext);
  const onSearch = React.useContext(ComboboxSearchContext);
  const append = hideTrigger ? null : (
    <>
      <ComboboxPrimitive.Trigger
        data-slot="combobox-trigger"
        render={
          <InputGroup.Button
            size="icon-xs"
            variant="ghost"
            data-slot="input-group-button"
            className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent [&_svg:not([class*='size-'])]:size-4"
            disabled={disabled}
          />
        }
      >
        <ChevronDownIcon className="text-muted-foreground size-4 pointer-events-none" />
      </ComboboxPrimitive.Trigger>
      {showClear && <ComboboxClear disabled={disabled} />}
    </>
  );
  return (
    <ComboboxPrimitive.Input
      render={({ ref, ...inputProps }) => {
        return (
          <InputGroup
            ref={mergeRefs(ref, forwardedRef)}
            data-disabled={disabled ? "true" : undefined}
            className={cn(
              "w-auto",
              "data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-transparent dark:data-[disabled=true]:bg-input/80",
              !searchable && "*:cursor-pointer *:caret-transparent *:select-none",
              className
            )}
            control={controlProps => (
              <Input
                {...controlProps}
                disabled={disabled}
                readOnly={!searchable}
                className="w-auto placeholder:text-muted-foreground"
                {...inputProps}
                onChange={e => {
                  (inputProps as React.InputHTMLAttributes<HTMLInputElement>).onChange?.(e);
                  onSearch?.(e.target.value);
                }}
                autoComplete="one-time-code"
              />
            )}
            prepend={icon}
            append={append}
          />
        );
      }}
      onFocus={e => {
        openOnFocus?.();
        onFocus?.(e);
      }}
      {...props}
    />
  );
});

function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, "side" | "align" | "sideOffset" | "alignOffset" | "anchor">) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-[6000]"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            "bg-popover text-popover-foreground overflow-hidden rounded-lg shadow-md data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 ring-foreground/10 *:data-[slot=input-group]:bg-input/30 *:data-[slot=input-group]:border-input/30 ring-1 duration-100 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:shadow-none data-[side=inline-start]:slide-in-from-right-2 data-[side=inline-end]:slide-in-from-left-2 group/combobox-content relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) data-[chips=true]:min-w-(--anchor-width)",
            "w-auto max-w-none",
            className
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxList({
  className,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  isSearchPending = false,
  ...props
}: ComboboxPrimitive.List.Props & {
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isSearchPending?: boolean;
}) {
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    const el = listRef.current;
    if (!el) return;

    const checkBottom = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        onLoadMore();
      }
    };

    el.addEventListener("scroll", checkBottom, { passive: true });
    checkBottom();
    return () => el.removeEventListener("scroll", checkBottom);
  }, [onLoadMore, hasMore, isLoadingMore]);

  return (
    <>
      <ComboboxPrimitive.List
        ref={listRef as React.Ref<HTMLDivElement>}
        data-slot="combobox-list"
        className={cn(
          "no-scrollbar max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 p-1 data-empty:p-0 overflow-y-auto overscroll-contain",
          className
        )}
        {...props}
      />
      {(isLoadingMore || isSearchPending) && (
        <div className="flex justify-center py-2">
          <Loader2Icon className="text-muted-foreground size-4 animate-spin" />
        </div>
      )}
    </>
  );
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "relative flex w-full cursor-default items-center rounded-md text-sm select-none",
        "data-highlighted:bg-accent data-highlighted:text-accent-foreground not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground gap-2 py-1 pr-8 pl-1.5 outline-hidden data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
            <CheckIcon className="pointer-events-none" />
          </span>
        }
      />
    </ComboboxPrimitive.Item>
  );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />;
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  const isLoading = React.useContext(ComboboxLoadingContext);
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        "text-muted-foreground hidden w-full justify-center py-2 text-center text-sm",
        !isLoading && "group-data-empty/combobox-content:flex",
        className
      )}
      {...props}
    />
  );
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

const ComboboxChips = React.forwardRef<HTMLDivElement, ComboboxPrimitive.Chips.Props>(function ComboboxChips(
  { className, ...props },
  ref
) {
  return (
    <ComboboxPrimitive.Chips
      ref={ref}
      data-slot="combobox-chips"
      className={cn(
        "group/input-group dark:bg-input/30 border-input focus-within:border-ring focus-within:ring-ring/50 has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40 has-aria-invalid:border-destructive dark:has-aria-invalid:border-destructive/50 flex min-h-8 flex-nowrap items-center gap-1 rounded-md border bg-transparent bg-clip-padding px-2.5 py-1 text-sm transition-colors focus-within:ring-3 has-aria-invalid:ring-3 has-data-[slot=combobox-chip]:px-1 overflow-x-auto no-scrollbar",
        className
      )}
      {...props}
    />
  );
});

function ComboboxChip({ className, children, ...props }: ComboboxPrimitive.Chip.Props) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      tabIndex={0}
      className={cn(
        "bg-slate-200 focus:bg-slate-300 text-accent-foreground flex h-[calc(--spacing(5.25))] w-fit items-center justify-center gap-1 rounded-sm px-1.5 text-xs font-medium whitespace-nowrap has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 cursor-pointer pr-0 outline-none",
        className
      )}
      onMouseDown={e => {
        if ((e.target as HTMLElement).closest('[data-slot="combobox-chip-remove"]')) {
          return;
        }
        e.preventDefault();
        e.preventBaseUIHandler();
        e.currentTarget.focus();
      }}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ChipRemove
        className={cn("-ml-1 bg-transparent hover:bg-transparent ")}
        data-slot="combobox-chip-remove"
        tabIndex={-1}
        onClick={e => {
          e.stopPropagation();
        }}
        onMouseDown={e => {
          e.stopPropagation();
        }}
        onPointerDown={e => {
          e.stopPropagation();
        }}
        render={
          <Button variant="ghost" size="icon-xs">
            <XIcon className="pointer-events-none" />
          </Button>
        }
      />
    </ComboboxPrimitive.Chip>
  );
}

function ComboboxChipsInput({
  className,
  disabled = false,
  hideTrigger = false,
  showClear = false,
  searchable = false,
  onFocus,
  onKeyDown,
  ...props
}: ComboboxPrimitive.Input.Props & {
  hideTrigger?: boolean;
  showClear?: boolean;
  searchable?: boolean;
}) {
  const openOnFocus = React.useContext(ComboboxOpenOnFocusContext);
  const onSearch = React.useContext(ComboboxSearchContext);
  return (
    <>
      <ComboboxPrimitive.Input
        data-slot="combobox-chip-input"
        render={inputProps => (
          <Input
            disabled={disabled}
            readOnly={!searchable}
            className={cn(
              "min-w-16 flex-1 border-0 h-auto py-0 px-1 shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent placeholder:text-muted-foreground",
              !searchable && "cursor-pointer caret-transparent select-none",
              className
            )}
            {...inputProps}
            onChange={e => {
              (inputProps as React.InputHTMLAttributes<HTMLInputElement>).onChange?.(e);
              onSearch?.(e.target.value);
            }}
            autoComplete="one-time-code"
          />
        )}
        onFocus={e => {
          openOnFocus?.();
          onFocus?.(e);
        }}
        onKeyDown={e => {
          const evt = e as React.KeyboardEvent<HTMLInputElement> & { preventBaseUIHandler?: () => void };
          if (evt.key === "Backspace" && evt.currentTarget.value === "") {
            const chipsContainer = evt.currentTarget.closest('[data-slot="combobox-chips"]');
            const chips = chipsContainer?.querySelectorAll<HTMLElement>('[data-slot="combobox-chip"]');
            if (chips && chips.length > 0) {
              evt.preventDefault();
              evt.preventBaseUIHandler?.();
              chips[chips.length - 1].focus();
            }
          }
          onKeyDown?.(e);
        }}
        {...props}
      />
      {hideTrigger || (
        <ComboboxPrimitive.Trigger
          data-slot="combobox-trigger"
          render={
            <InputGroup.Button
              size="icon-xs"
              variant="ghost"
              data-slot="input-group-button"
              className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent [&_svg:not([class*='size-'])]:size-4 ml-auto shrink-0"
              disabled={disabled}
            />
          }
        >
          <ChevronDownIcon className="text-muted-foreground size-4 pointer-events-none" />
        </ComboboxPrimitive.Trigger>
      )}
      {showClear && <ComboboxClear disabled={disabled} />}
    </>
  );
}

function useComboboxAnchor<T extends HTMLElement = HTMLDivElement>() {
  return React.useRef<T | null>(null);
}

Combobox.displayName = "Combobox";
ComboboxInput.displayName = "ComboboxInput";
ComboboxContent.displayName = "ComboboxContent";
ComboboxList.displayName = "ComboboxList";
ComboboxItem.displayName = "ComboboxItem";
ComboboxGroup.displayName = "ComboboxGroup";
ComboboxLabel.displayName = "ComboboxLabel";
ComboboxCollection.displayName = "ComboboxCollection";
ComboboxEmpty.displayName = "ComboboxEmpty";
ComboboxSeparator.displayName = "ComboboxSeparator";
ComboboxChips.displayName = "ComboboxChips";
ComboboxChip.displayName = "ComboboxChip";
ComboboxChipsInput.displayName = "ComboboxChipsInput";
ComboboxTrigger.displayName = "ComboboxTrigger";
ComboboxValue.displayName = "ComboboxValue";
ComboboxClear.displayName = "ComboboxClear";

const ComboboxNamespace = Object.assign(Combobox, {
  Input: ComboboxInput,
  Content: ComboboxContent,
  List: ComboboxList,
  Item: ComboboxItem,
  Group: ComboboxGroup,
  Label: ComboboxLabel,
  Collection: ComboboxCollection,
  Empty: ComboboxEmpty,
  Separator: ComboboxSeparator,
  Chips: ComboboxChips,
  Chip: ComboboxChip,
  ChipsInput: ComboboxChipsInput,
  Trigger: ComboboxTrigger,
  Value: ComboboxValue,
  Clear: ComboboxClear,
});

export { ComboboxNamespace as Combobox };
